import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { DEFAULT_SEO } from '../config/seo'

const ensureMetaTag = (identifier, content, preferredAttribute = 'name') => {
  if (!content) return
  const altAttribute = preferredAttribute === 'name' ? 'property' : 'name'
  let element = document.head.querySelector(`meta[${preferredAttribute}="${identifier}"]`)

  if (!element) {
    element = document.head.querySelector(`meta[${altAttribute}="${identifier}"]`)
    if (element) {
      preferredAttribute = element.getAttribute('name') ? 'name' : 'property'
    }
  }

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(preferredAttribute, identifier)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

const ensureLinkTag = (rel, href) => {
  if (!href) return
  let element = document.head.querySelector(`link[rel="${rel}"]`)

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

const normalizeUrl = (url) => {
  if (!url) return ''
  try {
    const normalized = new URL(url)
    return normalized.toString()
  } catch (error) {
    return url
  }
}

export const usePageMeta = (overrides = {}) => {
  const location = useLocation()

  useEffect(() => {
    if (typeof document === 'undefined') return

    const baseUrl = (overrides.baseUrl || DEFAULT_SEO.baseUrl || '').replace(/\/+$/, '')
    const path = overrides.path || `${location.pathname || ''}${location.search || ''}`
    const computedUrl = normalizeUrl(overrides.url || `${baseUrl}${path || '/'}` || baseUrl)
    const canonicalUrl = normalizeUrl(overrides.canonical || computedUrl)

    const title = overrides.title || DEFAULT_SEO.title
    const description = overrides.description || DEFAULT_SEO.description
    const image = overrides.image || DEFAULT_SEO.image
    const type = overrides.type || DEFAULT_SEO.type
    const siteName = overrides.siteName || DEFAULT_SEO.siteName
    const twitterCard = overrides.twitterCard || DEFAULT_SEO.twitterCard
    const robots = overrides.robots || DEFAULT_SEO.robots
    const twitterTitle = overrides.twitterTitle || title
    const twitterDescription = overrides.twitterDescription || description

    if (title) {
      document.title = title
      ensureMetaTag('og:title', title, 'property')
      ensureMetaTag('twitter:title', twitterTitle, 'name')
    }

    if (description) {
      ensureMetaTag('description', description, 'name')
      ensureMetaTag('og:description', description, 'property')
      ensureMetaTag('twitter:description', twitterDescription, 'name')
    }

    if (siteName) {
      ensureMetaTag('og:site_name', siteName, 'property')
    }

    if (image) {
      ensureMetaTag('og:image', image, 'property')
      ensureMetaTag('og:image:secure_url', image, 'property')
      ensureMetaTag('twitter:image', image, 'name')
    }

    if (type) {
      ensureMetaTag('og:type', type, 'property')
    }

    if (twitterCard) {
      ensureMetaTag('twitter:card', twitterCard, 'name')
    }

    ensureLinkTag('canonical', canonicalUrl)
    ensureMetaTag('og:url', canonicalUrl, 'property')
    ensureMetaTag('twitter:url', canonicalUrl, 'name')

    if (robots) {
      ensureMetaTag('robots', robots, 'name')
    }

    if (overrides.noindex) {
      ensureMetaTag('robots', 'noindex,nofollow', 'name')
    }
  }, [location.pathname, location.search, JSON.stringify(overrides)])
}

export default usePageMeta
